import React from 'react';
import PropTypes from 'prop-types';
import './Spinner.css';

/**
 * Attractive Spinner Component
 * 
 * @param {Object} props
 * @param {string} props.size - Size of the spinner: 'small', 'medium', 'large' (default: 'medium')
 * @param {string} props.variant - Spinner variant: 'circular', 'dots', 'pulse', 'bars', 'logo', 'progress' (default: 'circular')
 * @param {string} props.color - Color theme: 'primary', 'secondary', 'success', 'danger', 'info' (default: 'primary')
 * @param {string} props.text - Optional loading text to display below spinner
 * @param {boolean} props.fullScreen - Whether to show spinner in full screen overlay (default: false)
 * @param {boolean} props.transparent - Whether overlay should be transparent (default: false)
 * @param {number} props.progress - Progress percentage (0-100) for progress variant
 */
const Spinner = ({ 
  size = 'medium', 
  variant = 'circular', 
  color = 'primary',
  text = '',
  fullScreen = false,
  transparent = false,
  progress = 0
}) => {
  
  const renderSpinner = () => {
    switch(variant) {
      case 'progress':
        return (
          <div className={`spinner-progress spinner-${size} spinner-${color}`}>
            <div className="progress-circle-wrapper">
              <svg className="progress-ring" viewBox="0 0 120 120">
                <circle
                  className="progress-ring-bg"
                  cx="60"
                  cy="60"
                  r="54"
                />
                <circle
                  className="progress-ring-fill"
                  cx="60"
                  cy="60"
                  r="54"
                  style={{
                    strokeDasharray: `${2 * Math.PI * 54}`,
                    strokeDashoffset: `${2 * Math.PI * 54 * (1 - Math.min(Math.max(progress, 0), 100) / 100)}`,
                  }}
                />
              </svg>
              <div className="progress-percentage">
                {Math.round(Math.min(Math.max(progress, 0), 100))}%
              </div>
            </div>
          </div>
        );
      
      case 'logo':
        return (
          <div className={`spinner-logo spinner-${size}`}>
            <div className="logo-wrapper">
              <img src="/logo.svg" alt="Loading" className="spinner-logo-image" />
            </div>
            <div className="spinner-circle-wrapper">
              <div className="spinner-orbit"></div>
              <div className="spinner-orbit orbit-2"></div>
              <div className="spinner-orbit orbit-3"></div>
            </div>
          </div>
        );
      
      case 'dots':
        return (
          <div className={`spinner-dots spinner-${size} spinner-${color}`}>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        );
      
      case 'pulse':
        return (
          <div className={`spinner-pulse spinner-${size} spinner-${color}`}>
            <div className="pulse-ring"></div>
            <div className="pulse-ring"></div>
            <div className="pulse-ring"></div>
          </div>
        );
      
      case 'bars':
        return (
          <div className={`spinner-bars spinner-${size} spinner-${color}`}>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        );
      
      case 'circular':
      default:
        return (
          <div className={`spinner-circular spinner-${size} spinner-${color}`}>
            <div className="spinner-circle"></div>
          </div>
        );
    }
  };

  const spinnerContent = (
    <div className="spinner-container">
      {renderSpinner()}
      {text && <p className="spinner-text">{text}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className={`spinner-overlay ${transparent ? 'transparent' : ''}`}>
        {spinnerContent}
      </div>
    );
  }

  return spinnerContent;
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['circular', 'dots', 'pulse', 'bars', 'logo', 'progress']),
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'info']),
  text: PropTypes.string,
  fullScreen: PropTypes.bool,
  transparent: PropTypes.bool,
  progress: PropTypes.number
};

export default Spinner;
